"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  CompactTextArea,
  DraftPanel,
  FieldGroup,
  FormHeader,
} from "@/components/tools/academic-correspondence/kit";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildJobLetter,
  checkJobLetterLength,
  ENCLOSURES,
  type Enclosure,
  JOB_KINDS,
  JOB_LETTER_BANK,
  JOB_REGIONS,
  type JobKind,
  type JobLetterInput,
  type JobRegion,
  MAX_PAGES,
  WORDS_PER_PAGE,
} from "@/lib/tool-kit/admissions/academic-job-letter";
import { countPlaceholders } from "@/lib/tool-kit/correspondence/compose";
import { formatLetterDate } from "@/lib/tool-kit/correspondence/journal-cover-letter";

/** English strings. A translated content file overrides these through `ui`. */
export const ACADEMIC_JOB_COVER_LETTER_GENERATOR_STRINGS = {
  formHeading: "Your application",
  tryExample: "Try an example",
  clear: "Clear",
  regionLabel: "Convention",
  regionOptions: { us: "United States / Canada", uk: "United Kingdom" },
  regionHint:
    "Changes the greeting, sign-off, referee line, date format and length guidance. The posting's own instructions always win.",
  kindLabel: "Position",
  kindOptions: {
    faculty: "Faculty or lectureship",
    postdoc: "Postdoctoral position",
  },
  postSection: "The post",
  recipientLabel: "Addressed to (optional)",
  recipientPlaceholder: "Professor Lindqvist",
  recipientHint:
    "Include the title. Blank gives the usual committee greeting for your convention.",
  dateLabel: "Date (optional)",
  positionLabel: "Position title",
  positionPlaceholder: "Assistant Professor of Marine Ecology",
  departmentLabel: "Department or school",
  departmentPlaceholder: "Department of Biology",
  institutionLabel: "Institution",
  institutionPlaceholder: "Northfield University",
  referenceLabel: "Job reference (optional)",
  referencePlaceholder: "AB-1234",
  aboutSection: "You and your research",
  statusLabel: "Who you are now (optional)",
  statusPlaceholder:
    "a postdoctoral researcher at the University of Accra, studying how coral reefs recover from bleaching",
  statusHint: "Finishes the sentence “I am …”.",
  researchLabel: "Your research",
  researchPlaceholder:
    "What you study, what you have found, and why it matters — in your own words.",
  planLabel: "What you would do here (optional)",
  planPlaceholder:
    "build a long-term monitoring programme with local fishing communities",
  planHint: "Finishes “At [institution], I plan to …”.",
  teachingSection: "Teaching",
  teachingLabel: "Your teaching",
  teachingPlaceholder:
    "Courses taught, your approach, and one piece of evidence that it works.",
  coursesLabel: "Courses you could teach (optional)",
  coursesPlaceholder: "marine ecology, biostatistics, conservation biology",
  coursesHint: "Separate with commas.",
  newCourseLabel: "A course you would develop (optional)",
  newCoursePlaceholder: "community-based reef monitoring",
  fitSection: "Fit and service",
  fitLabel: "Why this department",
  fitPlaceholder:
    "Specific colleagues, centres, facilities or students — not praise for the university.",
  collaboratorsLabel: "People you would collaborate with (optional)",
  collaboratorsPlaceholder: "Dr. Mei Tan, Dr. Lucas Ferreira",
  collaboratorsHint: "Separate with commas.",
  serviceLabel: "Mentoring, service or outreach (optional)",
  servicePlaceholder: "I mentor undergraduate researchers…",
  enclosuresSection: "Enclosures",
  enclosureOptions: {
    cv: "Curriculum vitae",
    researchStatement: "Research statement",
    teachingStatement: "Teaching statement",
    writingSample: "Writing sample",
    supportingStatement: "Supporting statement",
  },
  refereesLabel: "Mention my referees",
  signSection: "Signature",
  signNameLabel: "Your name",
  signNamePlaceholder: "Adaeze Okafor",
  signTitleLabel: "Your current position",
  signTitlePlaceholder: "Postdoctoral Researcher, University of Accra",
  emailLabel: "Email (optional)",
  emailPlaceholder: "a.okafor@example.org",
  draftHeading: "Your cover letter",
  copy: "Copy",
  copyPlain: "Copy plain text",
  copied: "Copied!",
  download: "Download .txt",
  downloadFilename: "academic-job-cover-letter.txt",
  placeholdersLeft:
    "one:{count} placeholder left to fill — shown in [brackets]|other:{count} placeholders left to fill — shown in [brackets]",
  noPlaceholders: "No placeholders left in the letter.",
  lengthHeading: "Length",
  wordCount: "one:{count} word|other:{count} words",
  lengthLine: "{words}, about {pages} at {perPage} words a page.",
  pageCount: "one:{count} page|other:{count} pages",
  lengthGuide: {
    us: "Faculty letters in the US and Canada usually run one and a half to two pages, read alongside a CV and separate research and teaching statements.",
    uk: "UK academic letters are usually no more than two pages of A4, and may need to address a person specification or essential criteria.",
  },
  lengthStatus: {
    short: "Under a page: there is usually room to say more about fit.",
    ok: "Within the usual one to two pages.",
    long: "Over {max} pages: cut until it fits.",
  },
  example: {
    recipient: "",
    position: "Assistant Professor of Marine Ecology",
    department: "Department of Biology",
    institution: "Northfield University",
    reference: "",
    status:
      "a postdoctoral researcher at the University of Accra, studying how coral reefs recover from bleaching",
    research:
      "My research asks why some coral colonies recover after bleaching while their neighbours do not. In my doctoral work I combined photo-quadrat surveys with symbiont genotyping at 18 reef sites, and showed that local water quality predicts recovery better than temperature history alone. As a postdoctoral researcher I have extended this to a four-year monitoring dataset, which has produced three papers so far and a method now used by two regional monitoring teams. The work has been supported by a two-year national fellowship, and I have presented it at two international coral reef meetings. Beyond the papers, the project taught me to run a field programme: I trained eleven field assistants, managed permits across two national agencies, and kept a standing data-sharing agreement with the fishing cooperatives that host the surveys. My next questions concern what separates reefs that recover quickly from those that stall, and whether recovery can be steered by reducing local stressors such as sediment and nutrient runoff.",
    plan: "build a long-term reef monitoring programme with local fishing communities, linking survey data to management decisions, and apply to a national science funder in my first year",
    teaching:
      "I have taught marine ecology to second-year undergraduates for three years and designed a field course on survey methods. I teach from data: students collect, analyse and present their own survey results, and end-of-course evaluations in my sections have been among the highest in the department. I have supervised two honours theses to completion and I assess learning through short data reports rather than a single final examination, so students receive feedback while there is still time to act on it. I have also co-taught a graduate seminar on resilience ecology, and I would be glad to teach at both levels.",
    courses: "marine ecology, biostatistics, conservation biology",
    newCourse: "community-based reef monitoring",
    fit: "Northfield’s coastal field station and the Center for Coastal Studies would let me work on temperate and tropical systems side by side, which my current position does not allow. My work on recovery connects directly with the department’s long-term monitoring and fisheries research, and with the graduate students who run it. I am also drawn to the department’s commitment to training students in field and quantitative skills together, which matches how I mentor.",
    collaborators: "Dr. Mei Tan, Dr. Lucas Ferreira",
    service:
      "I mentor undergraduate researchers each year, including students from groups under-represented in marine science, and I co-organise a workshop on open data practices for early-career ecologists.",
    signName: "Adaeze Okafor",
    signTitle: "Postdoctoral Researcher, University of Accra",
    email: "a.okafor@example.org",
  },
  ...JOB_LETTER_BANK,
} as const;

interface Form extends Omit<JobLetterInput, "date"> {
  dateIso: string;
}

const NO_ENCLOSURES: Record<Enclosure, boolean> = {
  cv: true,
  researchStatement: false,
  teachingStatement: false,
  writingSample: false,
  supportingStatement: false,
};

const EMPTY: Form = {
  dateIso: "",
  region: "us",
  kind: "faculty",
  recipient: "",
  position: "",
  department: "",
  institution: "",
  reference: "",
  status: "",
  research: "",
  plan: "",
  teaching: "",
  courses: "",
  newCourse: "",
  fit: "",
  collaborators: "",
  service: "",
  enclosed: NO_ENCLOSURES,
  referees: false,
  signName: "",
  signTitle: "",
  email: "",
};

type TextKey = Exclude<
  keyof Form,
  "region" | "kind" | "enclosed" | "referees" | "dateIso"
>;

export default function AcademicJobCoverLetterGeneratorWidget() {
  const strings = useToolUi(ACADEMIC_JOB_COVER_LETTER_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [form, setForm] = useState<Form>(EMPTY);
  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const text = (key: TextKey, id: string) => ({
    id: `ajl-${id}`,
    onChange: (value: string) => set(key, value),
    value: form[key],
  });

  const paragraphs = useMemo(
    () =>
      buildJobLetter(
        {
          ...form,
          date: formatLetterDate(
            form.dateIso,
            form.region === "uk" && locale === "en" ? "en-GB" : locale
          ),
        },
        strings,
        locale
      ),
    [form, locale, strings]
  );
  const length = checkJobLetterLength(paragraphs);
  const blanks = countPlaceholders(paragraphs);
  const status =
    blanks > 0
      ? tPlural(strings.placeholdersLeft, locale, blanks, {
          count: fmt(blanks),
        })
      : strings.noPlaceholders;
  const words = tPlural(strings.wordCount, locale, length.words, {
    count: fmt(length.words),
  });

  return (
    <>
      <section className="space-y-5">
        <FormHeader
          clearLabel={strings.clear}
          exampleLabel={strings.tryExample}
          heading={strings.formHeading}
          onClear={() => setForm(EMPTY)}
          onExample={() =>
            setForm({
              ...EMPTY,
              ...strings.example,
              enclosed: {
                ...NO_ENCLOSURES,
                researchStatement: true,
                teachingStatement: true,
              },
              referees: true,
            })
          }
        />
        <div className="grid gap-5 sm:grid-cols-2">
          <PillGroup
            label={strings.regionLabel}
            name="ajl-region"
            onChange={(value: JobRegion) => set("region", value)}
            options={JOB_REGIONS.map((key) => ({
              value: key,
              label: strings.regionOptions[key],
            }))}
            value={form.region}
          />
          <PillGroup
            label={strings.kindLabel}
            name="ajl-kind"
            onChange={(value: JobKind) => set("kind", value)}
            options={JOB_KINDS.map((key) => ({
              value: key,
              label: strings.kindOptions[key],
            }))}
            value={form.kind}
          />
        </div>
        <p className={HINT}>{strings.regionHint}</p>

        <FieldGroup title={strings.postSection}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              {...text("position", "position")}
              label={strings.positionLabel}
              placeholder={strings.positionPlaceholder}
            />
            <TextField
              {...text("department", "department")}
              label={strings.departmentLabel}
              placeholder={strings.departmentPlaceholder}
            />
            <TextField
              {...text("institution", "institution")}
              label={strings.institutionLabel}
              placeholder={strings.institutionPlaceholder}
            />
            <TextField
              {...text("reference", "reference")}
              label={strings.referenceLabel}
              placeholder={strings.referencePlaceholder}
            />
            <TextField
              {...text("recipient", "recipient")}
              hint={strings.recipientHint}
              label={strings.recipientLabel}
              placeholder={strings.recipientPlaceholder}
            />
            <TextField
              id="ajl-date"
              label={strings.dateLabel}
              onChange={(value) => set("dateIso", value)}
              type="date"
              value={form.dateIso}
            />
          </div>
        </FieldGroup>

        <FieldGroup title={strings.aboutSection}>
          <CompactTextArea
            {...text("status", "status")}
            hint={strings.statusHint}
            label={strings.statusLabel}
            placeholder={strings.statusPlaceholder}
            rows={2}
          />
          <CompactTextArea
            {...text("research", "research")}
            label={strings.researchLabel}
            placeholder={strings.researchPlaceholder}
            rows={6}
          />
          <CompactTextArea
            {...text("plan", "plan")}
            hint={strings.planHint}
            label={strings.planLabel}
            placeholder={strings.planPlaceholder}
            rows={2}
          />
        </FieldGroup>

        <FieldGroup title={strings.teachingSection}>
          <CompactTextArea
            {...text("teaching", "teaching")}
            label={strings.teachingLabel}
            placeholder={strings.teachingPlaceholder}
            rows={4}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              {...text("courses", "courses")}
              hint={strings.coursesHint}
              label={strings.coursesLabel}
              placeholder={strings.coursesPlaceholder}
            />
            <TextField
              {...text("newCourse", "new-course")}
              label={strings.newCourseLabel}
              placeholder={strings.newCoursePlaceholder}
            />
          </div>
        </FieldGroup>

        <FieldGroup title={strings.fitSection}>
          <CompactTextArea
            {...text("fit", "fit")}
            label={strings.fitLabel}
            placeholder={strings.fitPlaceholder}
            rows={4}
          />
          <TextField
            {...text("collaborators", "collaborators")}
            hint={strings.collaboratorsHint}
            label={strings.collaboratorsLabel}
            placeholder={strings.collaboratorsPlaceholder}
          />
          <CompactTextArea
            {...text("service", "service")}
            label={strings.serviceLabel}
            placeholder={strings.servicePlaceholder}
            rows={2}
          />
        </FieldGroup>

        <FieldGroup title={strings.enclosuresSection}>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {ENCLOSURES.map((key) => (
              <Checkbox
                checked={form.enclosed[key]}
                key={key}
                label={strings.enclosureOptions[key]}
                onChange={(checked) =>
                  set("enclosed", { ...form.enclosed, [key]: checked })
                }
              />
            ))}
            <Checkbox
              checked={form.referees}
              label={strings.refereesLabel}
              onChange={(checked) => set("referees", checked)}
            />
          </div>
        </FieldGroup>

        <FieldGroup title={strings.signSection}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              {...text("signName", "sign-name")}
              label={strings.signNameLabel}
              placeholder={strings.signNamePlaceholder}
            />
            <TextField
              {...text("signTitle", "sign-title")}
              label={strings.signTitleLabel}
              placeholder={strings.signTitlePlaceholder}
            />
            <TextField
              {...text("email", "email")}
              label={strings.emailLabel}
              placeholder={strings.emailPlaceholder}
            />
          </div>
        </FieldGroup>
      </section>

      <DraftPanel
        filename={strings.downloadFilename}
        heading={strings.draftHeading}
        paragraphs={paragraphs}
        status={status}
        strings={strings}
      >
        <section aria-live="polite" className="space-y-1">
          <h3 className="font-semibold text-foreground text-lg">
            {strings.lengthHeading}
          </h3>
          <p className="text-foreground text-sm">
            {t(strings.lengthLine, {
              words,
              pages: tPlural(strings.pageCount, locale, length.pages, {
                count: fmt(length.pages, { maximumFractionDigits: 1 }),
              }),
              perPage: fmt(WORDS_PER_PAGE),
            })}{" "}
            {t(strings.lengthStatus[length.status], { max: fmt(MAX_PAGES) })}
          </p>
          <p className={HINT}>{strings.lengthGuide[form.region]}</p>
        </section>
      </DraftPanel>
    </>
  );
}
